import React, { useEffect, useMemo, useState } from "react";
import {
	Box,
	CircularProgress,
	Dialog,
	DialogContent,
	DialogTitle,
	Divider,
	IconButton,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableRow,
	Tooltip,
	Typography,
} from "@mui/material";
import {
	Close as CloseIcon,
	ContentCopy as CopyIcon,
	DescriptionOutlined as DescriptionIcon,
} from "@mui/icons-material";
import ReactJson from "react-json-view";
import { ToastContent } from "../../../../shared/components";
import { useToast } from "../../../../shared/hooks";
import { ISerasaResponse, serasaService } from "../../../../shared/services/api/serasa/serasaService";
import { returnMessageError } from "../../../../shared/utils/returnMessageError";

export interface ModalLogSerasaAnalysisProps {
	isOpen: boolean;
	onClose: () => void;
	analysisId: string;
	reasonId?: string | null;
}

const convertStatusSerasa = (status?: string | null) => {
	if (!status) return "Não fornecida";
	if (status === "approved") return "Aprovado";
	if (status === "refused") return "Reprovado";
	return status;
};

const looksLikeJson = (text: string) => {
	const t = text.trim();
	if (!t) return false;
	const first = t.charAt(0);
	const second = t.charAt(1);
	const last = t.charAt(t.length - 1);
	const penultimate = t.charAt(t.length - 2);

	const startsOk = first === "{" || first === "[" || (first === '"' && (second === "{" || second === "["));
	const endsOk = last === "}" || last === "]" || (last === '"' && (penultimate === "}" || penultimate === "]"));
	return startsOk && endsOk;
};

const tryParsePossiblyEncodedJson = (text: string): unknown | undefined => {
	const t = text.trim();
	if (!t) return undefined;
	if (!looksLikeJson(t) && !t.includes("\\\"") && !t.includes("\\\\")) return undefined;

	const tryParse = (s: string): unknown | undefined => {
		try {
			return JSON.parse(s);
		} catch {
			return undefined;
		}
	};

	const parsed = tryParse(t);
	if (parsed !== undefined) {
		if (typeof parsed === "string") {
			const inner = parsed.trim();
			const parsedInner = tryParse(inner);
			if (parsedInner !== undefined) return parsedInner;
		}
		return parsed;
	}

	const unescapedQuotes = t.replace(/\\\"/g, '"');
	const parsedUnescaped = tryParse(unescapedQuotes);
	if (parsedUnescaped !== undefined) return parsedUnescaped;

	const reducedBackslashes = t.replace(/\\\\/g, "\\").replace(/\\\"/g, '"');
	const parsedReduced = tryParse(reducedBackslashes);
	if (parsedReduced !== undefined) return parsedReduced;

	return undefined;
};

const normalizeJsonLikeStrings = (value: unknown): unknown => {
	if (typeof value === "string") {
		const parsed = tryParsePossiblyEncodedJson(value);
		if (parsed !== undefined && (typeof parsed === "object" || Array.isArray(parsed))) {
			return normalizeJsonLikeStrings(parsed);
		}
		return value;
	}

	if (Array.isArray(value)) {
		return value.map((item) => normalizeJsonLikeStrings(item));
	}

	if (value && typeof value === "object") {
		const record = value as Record<string, unknown>;
		const normalized: Record<string, unknown> = {};
		Object.keys(record).forEach((key) => {
			normalized[key] = normalizeJsonLikeStrings(record[key]);
		});
		return normalized;
	}

	return value;
};

export const ModalLogSerasaAnalysis: React.FC<ModalLogSerasaAnalysisProps> = ({
	isOpen,
	onClose,
	analysisId,
	reasonId,
}) => {
	const [isLoading, setIsLoading] = useState(false);
	const [serasaResponse, setSerasaResponse] = useState<ISerasaResponse | undefined>();
	const [rawPayload, setRawPayload] = useState<unknown>(undefined);
	const toast = useToast();

	useEffect(() => {
		if (!isOpen) return;

		const fetchLog = async () => {
			setIsLoading(true);
			try {
				const result = await serasaService.getSerasaByAnalysisId(analysisId);
				if (result instanceof Error) throw result;

				const parsed = (() => {
					const data = result.data?.data;
					if (typeof data !== "string") return undefined;
					try {
						return JSON.parse(data) as ISerasaResponse;
					} catch {
						return undefined;
					}
				})();

				const normalized = normalizeJsonLikeStrings(parsed ?? result.data?.data ?? {});
				setRawPayload(normalized);
				setSerasaResponse((parsed ?? normalized) as any);

			} catch (err) {
				const { titleMessage, subtitleMessage, contentMessage } = returnMessageError(err);
				toast.present({
					type: "error",
					position: "top-right",
					durationMs: 4000,
					messageOrContent: <ToastContent title={titleMessage} subtitle={subtitleMessage} content={contentMessage} />,
				});
			} finally {
				setIsLoading(false);
			}
		};

		fetchLog();
	}, [isOpen, analysisId]);

	const payloadString = useMemo(() => {
		try {
			return JSON.stringify(rawPayload ?? serasaResponse ?? {}, null, 2);
		} catch {
			return "";
		}
	}, [rawPayload, serasaResponse]);

	const handleCopy = async () => {
		try {
			await navigator.clipboard.writeText(payloadString);
			toast.present({
				type: "success",
				position: "top-right",
				durationMs: 2500,
				messageOrContent: <ToastContent title="Copiado!" content="Payload do Serasa copiado." />,
			});
		} catch (err) {
			const { titleMessage, subtitleMessage, contentMessage } = returnMessageError(err);
			toast.present({
				type: "error",
				position: "top-right",
				durationMs: 4000,
				messageOrContent: <ToastContent title={titleMessage} subtitle={subtitleMessage} content={contentMessage} />,
			});
		}
	};

	return (
		<Dialog open={isOpen} onClose={onClose} maxWidth="md" fullWidth>
			<DialogTitle sx={{ pb: 1 }}>
				<Box display="flex" alignItems="center" justifyContent="space-between" pr={0.5}>
					<Box display="flex" alignItems="center" gap={1.5}>
						<DescriptionIcon sx={{ fontSize: 30, color: "primary.main" }} />
						<Box>
							<Typography variant="h6" fontWeight={600} color="primary.main">
								Log do Serasa
							</Typography>
							<Typography variant="caption" color="text.secondary">
								Análise: {analysisId}
							</Typography>
						</Box>
					</Box>

					<Box display="flex" gap={0.5}>
						<Tooltip title="Copiar payload (JSON formatado)">
							<span>
								<IconButton onClick={handleCopy} disabled={!payloadString || isLoading}>
									<CopyIcon />
								</IconButton>
							</span>
						</Tooltip>
						<Tooltip title="Fechar">
							<IconButton onClick={onClose}>
								<CloseIcon />
							</IconButton>
						</Tooltip>
					</Box>
				</Box>
			</DialogTitle>

			<DialogContent>
				{isLoading ? (
					<Box display="flex" justifyContent="center" py={4}>
						<CircularProgress />
					</Box>
				) : (
					<>
						<Paper elevation={0} sx={{ border: "1px solid #e7e7e7", borderRadius: 2, overflow: "hidden" }}>
							<Box px={2} py={1.5} bgcolor="#fafafa">
								<Typography variant="subtitle2" fontWeight={600}>
									Resumo
								</Typography>
							</Box>
							<Divider />
							<Table size="small">
								<TableBody>
									<TableRow>
										<TableCell sx={{ width: 230, color: "text.secondary" }}>Razão</TableCell>
										<TableCell>{reasonId || "Não fornecida"}</TableCell>
									</TableRow>
									<TableRow>
										<TableCell sx={{ color: "text.secondary" }}>Status do Serasa</TableCell>
										<TableCell>{convertStatusSerasa(serasaResponse?.status)}</TableCell>
									</TableRow>
									<TableRow>
										<TableCell sx={{ color: "text.secondary" }}>Erro (Serasa)</TableCell>
										<TableCell>{serasaResponse?.serasa_response?.error || "Não fornecido"}</TableCell>
									</TableRow>
									<TableRow>
										<TableCell sx={{ color: "text.secondary" }}>Mensagem (Serasa)</TableCell>
										<TableCell>{serasaResponse?.serasa_response?.message || "Não fornecida"}</TableCell>
									</TableRow>
									<TableRow>
										<TableCell sx={{ color: "text.secondary" }}>Descrição (Serasa)</TableCell>
										<TableCell>{serasaResponse?.serasa_response?.message_description || "Não fornecido"}</TableCell>
									</TableRow>
								</TableBody>
							</Table>
						</Paper>

						<Box mt={2}>
							<Typography variant="subtitle2" fontWeight={600} mb={1}>
								Payload (JSON)
							</Typography>
							<Box
								sx={{
									maxHeight: "55vh",
									overflow: "auto",
									backgroundColor: "#1e1e1e",
									borderRadius: 2,
									p: 2,
								}}
							>
								{rawPayload && (typeof rawPayload === "object" || Array.isArray(rawPayload)) ? (
									<ReactJson
										src={rawPayload as any}
										displayDataTypes={false}
										quotesOnKeys={false}
										collapsed={2}
										name={false}
										theme="monokai"
									/>
								) : (
									<Box
										sx={{
											color: "#d6e6ff",
											fontFamily: "monospace",
											whiteSpace: "pre-wrap",
											fontSize: 14,
										}}
									>
										{payloadString || "{}"}
									</Box>
								)}
							</Box>
						</Box>
					</>
				)}
			</DialogContent>
		</Dialog>
	);
};
